// The periods the Time Period picker offers: reporting years, which use the start and end saved in
// the annual reports, and rolling periods, which are whole days in the group's time zone

import { TZDate } from "@date-fns/tz";
import { addDays as addDaysToDate, addMonths as addMonthsToDate, format } from "date-fns";
import { SavedReportingYear } from "../../types/dashboardConfig";

export interface CalendarDate {
  year: number;
  // 1 to 12
  month: number;
  day: number;
}

export interface Period {
  key: string;
  label: string;
  // The first and last day, as the group reads them
  firstDay: CalendarDate;
  lastDay: CalendarDate;
  // The exact first and last moments the API counts activities between, in UTC like "2026-04-21T07:00:00Z"
  start: string;
  end: string;
  // Compared with this period when the comparison follows the focus, for a year-over-year view.
  // Periods made of days have one; a reporting year is compared with the reporting year before it
  sameDatesLastYear?: Period;
}

export interface RollingPeriod extends Period {
  sameDatesLastYear: Period;
}

const ROLLING_MONTH_COUNTS = [12, 9, 6];

// The calendar and date-fns work with dates on the device, which are only read back as year, month
// and day, so the device's time zone never changes which day it is
export const toDate = ({ year, month, day }: CalendarDate): Date => new Date(year, month - 1, day);
export const toCalendarDate = (date: Date): CalendarDate => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
});

// A day that doesn't exist in the new month, like Feb 29 a year earlier, becomes its last day
const addMonths = (date: CalendarDate, months: number): CalendarDate =>
  toCalendarDate(addMonthsToDate(toDate(date), months));

const addDays = (date: CalendarDate, days: number): CalendarDate =>
  toCalendarDate(addDaysToDate(toDate(date), days));

// The UTC moment at which a clock in the time zone reads the given day and time. TZDate's own
// toISOString would give the time zone's offset instead of Z
const toUtcMoment = (
  { year, month, day }: CalendarDate,
  [hours, minutes, seconds]: number[],
  timeZone: string,
): string =>
  new Date(new TZDate(year, month - 1, day, hours, minutes, seconds, 0, timeZone).getTime())
    .toISOString()
    .replace(".000Z", "Z");

const createPeriod = (
  key: string,
  label: string,
  firstDay: CalendarDate,
  lastDay: CalendarDate,
  timeZone: string,
): Period => ({
  key,
  label,
  firstDay,
  lastDay,
  start: toUtcMoment(firstDay, [0, 0, 0], timeZone),
  end: toUtcMoment(lastDay, [23, 59, 59], timeZone),
});

// Not read from a TZDate, whose getDate and the like return UTC values under this repo's jest setup
export const getTodayInTimeZone = (now: Date, timeZone: string): CalendarDate => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(now);
  const { year, month, day } = Object.fromEntries(
    parts.map((part) => [part.type, Number(part.value)]),
  );
  return { year, month, day };
};

// A reporting year is exactly the start and end saved in its annual report, so the dashboard counts
// the same activities as the Annual Report, even for the few reports saved a little off midnight
export const createReportingYearPeriod = (report: SavedReportingYear): Period => {
  const parseDisplayDate = (displayDate: string): CalendarDate => {
    const [month, day, year] = displayDate.split("/").map(Number);
    return { year, month, day };
  };
  return {
    key: `reporting-year-${report.reportingYear}`,
    label: `Reporting Year ${report.reportingYear}`,
    firstDay: parseDisplayDate(report.displayDataRangeFirst),
    lastDay: parseDisplayDate(report.displayDataRangeLast),
    start: report.dataRangeFirst,
    end: report.dataRangeLast,
  };
};

// Each period runs from the first moment of its first day to the last second of today, so today counts
export const createRollingPeriods = (today: CalendarDate, timeZone: string): RollingPeriod[] => {
  const definitions = [
    ...ROLLING_MONTH_COUNTS.map((months) => ({
      key: `past-${months}-months`,
      label: `Past ${months} Months`,
      firstDay: addDays(addMonths(today, -months), 1),
    })),
    {
      key: "year-to-date",
      label: "Year to Date",
      firstDay: { year: today.year, month: 1, day: 1 },
    },
  ];

  return definitions.map(({ key, label, firstDay }) => {
    const period = createPeriod(key, label, firstDay, today, timeZone);
    return { ...period, sameDatesLastYear: createEarlierYearPeriod(period, 1, timeZone) };
  });
};

// A range of days someone picked. Its label is its dates, since "Custom Range" wouldn't tell the
// focus from the comparison on a chart
export const createCustomPeriod = (
  key: string,
  firstDay: CalendarDate,
  lastDay: CalendarDate,
  timeZone: string,
): Period => {
  const dated = createPeriod(key, "", firstDay, lastDay, timeZone);
  const period = { ...dated, label: formatFullRange(dated) };
  return { ...period, sameDatesLastYear: createEarlierYearPeriod(period, 1, timeZone) };
};

// The same days some years earlier. Feb 29 becomes Feb 28
const createEarlierYearPeriod = (
  period: Period,
  yearsEarlier: number,
  timeZone: string,
): Period => {
  const dated = createPeriod(
    yearsEarlier === 1 ? `${period.key}-last-year` : `${period.key}-${yearsEarlier}-years-earlier`,
    "",
    addMonths(period.firstDay, -12 * yearsEarlier),
    addMonths(period.lastDay, -12 * yearsEarlier),
    timeZone,
  );
  return { ...dated, label: formatFullRange(dated) };
};

// The same days in each of the years before a period made of days, nearest year first, which is
// what such a period can be compared with
export const createEarlierYearPeriods = (
  period: Period,
  count: number,
  timeZone: string,
): Period[] =>
  Array.from({ length: count }, (_, index) => createEarlierYearPeriod(period, index + 1, timeZone));

// The reporting year before a reporting year, or the same dates a year earlier for a period made of
// days. The oldest reporting year can only be compared with itself
export const getAutoMatchedComparison = (focusPeriod: Period, reportingYears: Period[]): Period => {
  if (focusPeriod.sameDatesLastYear) return focusPeriod.sameDatesLastYear;

  const focusIndex = reportingYears.findIndex((period) => period.key === focusPeriod.key);
  if (focusIndex === -1) throw new Error(`Unknown period ${focusPeriod.key}`);
  return reportingYears[focusIndex + 1] ?? focusPeriod;
};

// Like "Oct 1, 2025"
export const formatDay = (date: CalendarDate): string => format(toDate(date), "MMM d, yyyy");

// Like "Oct 1, 2025 – Sep 30, 2026"
export const formatFullRange = ({ firstDay, lastDay }: Period): string =>
  `${formatDay(firstDay)} – ${formatDay(lastDay)}`;
